/**
 * PolicyRule Form Layout
 *
 * Dual-mode layout:
 * - Standard mode: simplified 4-section form (Rule Details, Scope, Permissions, Attributes)
 * - Admin mode: full 7-section form with all fields and Id
 */

import React from 'react';
import { Alert, Box, Text, Grid, Divider } from '@mantine/core';
import { IconInfoCircle } from '@tabler/icons-react';
import { createEntityRef } from '../../../types';
import { getEntityValueFromARef } from '../../../types';
import { getCachedRef, isARef } from '../../../utils/entityPropertyUtils';
import { hasFormValueForEntityRef } from '../../../types/entity/EntityRef';

export interface PolicyRuleFormLayoutProps {
    schemaAttributes: any[];
    formData: Record<string, any>;
    relativeTargetAttributeError: string | null;
    renderField: (attr: any) => React.ReactNode;
    isStandard?: boolean;
}

const POLICY_RULE_FIELD_ORDER = [
    'DisplayName',
    'Description',
    'RequestorSet',
    'TargetSet',
    'RelativeTargetAttribute',
    'TargetPostOpSet',
    'RequestWorkflow',
    'AuthorizationWorkflow',
    'Attributes',
    'ReadEntity',
    'CreateEntity',
    'ModifyEntity',
    'DeleteEntity',
    'AddValue',
    'DeleteValue',
    'RemoveValue',
    'SetValue',
    'Decrypt',
    'Disabled'
];

const RELATIVE_TARGET_ATTR = getCachedRef('RelativeTargetAttribute');
const REQUESTOR_SET_ATTR = getCachedRef('RequestorSet');

function normName(raw: string): string {
    const s = (raw ?? '').toString().trim().toLowerCase().replace(/\s+/g, '');
    return s.startsWith('$.') ? s.slice(2) : s;
}

function sortByFieldOrder(attributes: any[]): any[] {
    return [...attributes].sort((a, b) => {
        const aName = normName((a.name || a.id || a.value || ''));
        const bName = normName((b.name || b.id || b.value || ''));
        const aIndex = POLICY_RULE_FIELD_ORDER.findIndex(field => field.toLowerCase().replace(/\s+/g, '') === aName);
        const bIndex = POLICY_RULE_FIELD_ORDER.findIndex(field => field.toLowerCase().replace(/\s+/g, '') === bName);

        if (aIndex >= 0 && bIndex >= 0) return aIndex - bIndex;
        if (aIndex >= 0) return -1;
        if (bIndex >= 0) return 1;
        return 0;
    });
}

const OPERATION_FIELD_NAMES = [
    'readentity', 'createentity', 'modifyentity', 'deleteentity', 'addvalue', 'deletevalue', 'removevalue', 'setvalue', 'decrypt'
];

/** Fields hidden in standard mode (advanced intersect/workflow features). */
const STANDARD_HIDDEN_FIELDS = new Set([
    'relativetargetattribute', 'targetpostopset', 'requestworkflow', 'authorizationworkflow'
]);

function categorizeFields(schemaAttributes: any[], isStandard: boolean) {
    const withoutAllAttributes = schemaAttributes.filter(
        (attr: any) => !isARef(createEntityRef(attr.name || attr.id || attr.value || ''), 'AllAttributes')
    );

    const filtered = isStandard
        ? withoutAllAttributes.filter((attr: any) => {
            const n = normName(attr.name || attr.id || attr.value || '');
            return !STANDARD_HIDDEN_FIELDS.has(n);
        })
        : withoutAllAttributes;

    const sortedAttributes = sortByFieldOrder(filtered);

    const idFields: any[] = [];
    const basicFields: any[] = [];
    const requestorFields: any[] = [];
    const targetFields: any[] = [];
    const workflowFields: any[] = [];
    const attributeFields: any[] = [];
    const permissionFields: any[] = [];
    const statusFields: any[] = [];

    sortedAttributes.forEach((attr: any) => {
        const name = attr.name || attr.id || attr.value || '';
        const attrRef = createEntityRef(name);

        if (isARef(attrRef, 'Id')) {
            idFields.push(attr);
        } else if (isARef(attrRef, 'DisplayName') || isARef(attrRef, 'Description')) {
            basicFields.push(attr);
        } else if (isARef(attrRef, 'RequestorSet')) {
            requestorFields.push(attr);
        } else if (isARef(attrRef, 'TargetSet') || isARef(attrRef, 'TargetPostOpSet') || isARef(attrRef, 'RelativeTargetAttribute')) {
            targetFields.push(attr);
        } else if (isARef(attrRef, 'RequestWorkflow') || isARef(attrRef, 'AuthorizationWorkflow')) {
            workflowFields.push(attr);
        } else if (isARef(attrRef, 'Attributes')) {
            attributeFields.push(attr);
        } else if (OPERATION_FIELD_NAMES.some((op) => isARef(attrRef, op))) {
            permissionFields.push(attr);
        } else if (isARef(attrRef, 'Disabled')) {
            statusFields.push(attr);
        } else {
            basicFields.push(attr);
        }
    });

    return {
        idFields,
        basicFields,
        requestorFields,
        targetFields,
        workflowFields,
        attributeFields,
        permissionFields,
        statusFields
    };
}

function SystemRuleBanner({ formData }: { formData: Record<string, any> }) {
    const isSystemRaw = getEntityValueFromARef('IsSystem', formData);
    const isSystem = isSystemRaw === true || isSystemRaw === 'true' || isSystemRaw === 'True';
    if (!isSystem) return null;
    return (
        <Alert icon={<IconInfoCircle size={18} />} color="blue" variant="light" mb="lg">
            This is a system-managed policy rule. Changes may be overwritten by the system.
        </Alert>
    );
}

function StandardLayout({
    idFields: _idFields,
    basicFields,
    requestorFields,
    targetFields,
    attributeFields,
    permissionFields,
    statusFields,
    formData,
    renderField
}: {
    idFields: any[];
    basicFields: any[];
    requestorFields: any[];
    targetFields: any[];
    attributeFields: any[];
    permissionFields: any[];
    statusFields: any[];
    formData: Record<string, any>;
    renderField: (attr: any) => React.ReactNode;
}) {
    const scopeFields = [
        ...requestorFields,
        ...targetFields.filter((attr: any) => {
            const ref = createEntityRef(attr.name || attr.id || attr.value || '');
            return isARef(ref, 'TargetSet');
        })
    ];

    return (
        <>
            <SystemRuleBanner formData={formData} />

            {/* Section 1: Rule Details — DisplayName, Description, Disabled */}
            <Box mb="lg">
                <Text fw={600} size="md" mb="xs">Rule Details</Text>
                <Grid gutter="md">
                    {basicFields.map((attr, index) => (
                        <Grid.Col span={12} key={index}>
                            {renderField(attr)}
                        </Grid.Col>
                    ))}
                    {statusFields.map((attr, index) => (
                        <Grid.Col span={12} key={`status-${index}`}>
                            {renderField(attr)}
                        </Grid.Col>
                    ))}
                </Grid>
            </Box>

            <Divider my="lg" />

            {/* Section 2: Scope — RequestorSet + TargetSet side by side */}
            {scopeFields.length > 0 && (
                <>
                    <Box mb="lg">
                        <Text fw={600} size="md" mb="xs">Scope</Text>
                        <Text size="sm" c="dimmed" mb="md">
                            Define who can perform the action and what entities are affected.
                        </Text>
                        <Grid gutter="md">
                            {scopeFields.map((attr, index) => (
                                <Grid.Col span={{ base: 12, sm: 6 }} key={index}>
                                    {renderField(attr)}
                                </Grid.Col>
                            ))}
                        </Grid>
                    </Box>
                    <Divider my="lg" />
                </>
            )}

            {/* Section 3: Permissions — 3-column grid */}
            {permissionFields.length > 0 && (
                <>
                    <Box mb="lg">
                        <Text fw={600} size="md" mb="xs">Permissions</Text>
                        <Text size="sm" c="dimmed" mb="md">
                            Select which operations this rule allows.
                        </Text>
                        <Grid gutter="md">
                            {permissionFields.map((attr, index) => (
                                <Grid.Col span={{ base: 12, sm: 4 }} key={index}>
                                    {renderField(attr)}
                                </Grid.Col>
                            ))}
                        </Grid>
                    </Box>
                    <Divider my="lg" />
                </>
            )}

            {/* Section 4: Attributes — full width */}
            {attributeFields.length > 0 && (
                <Box mb="lg">
                    <Text fw={600} size="md" mb="xs">Attributes</Text>
                    <Text size="sm" c="dimmed" mb="md">
                        Choose which attributes this policy rule applies to.
                    </Text>
                    {attributeFields.map((attr, index) => (
                        <React.Fragment key={index}>{renderField(attr)}</React.Fragment>
                    ))}
                </Box>
            )}
        </>
    );
}

function AdminLayout({
    idFields,
    basicFields,
    requestorFields,
    targetFields,
    workflowFields,
    attributeFields,
    permissionFields,
    statusFields,
    formData,
    relativeTargetAttributeError,
    renderField
}: {
    idFields: any[];
    basicFields: any[];
    requestorFields: any[];
    targetFields: any[];
    workflowFields: any[];
    attributeFields: any[];
    permissionFields: any[];
    statusFields: any[];
    formData: Record<string, any>;
    relativeTargetAttributeError: string | null;
    renderField: (attr: any) => React.ReactNode;
}) {
    return (
        <>
            <SystemRuleBanner formData={formData} />

            {/* Id field (admin only) */}
            {idFields.length > 0 && (
                <Box mb="md">
                    {idFields.map((attr, index) => (
                        <React.Fragment key={index}>{renderField(attr)}</React.Fragment>
                    ))}
                </Box>
            )}

            {/* Basic Information */}
            <Box mb="lg">
                <Grid gutter="md">
                    {basicFields.map((attr, index) => (
                        <Grid.Col span={12} key={index}>
                            {renderField(attr)}
                        </Grid.Col>
                    ))}
                </Grid>
            </Box>

            <Divider my="lg" />

            {/* Requestor Configuration */}
            {requestorFields.length > 0 && (
                <>
                    <Box mb="lg">
                        <Text fw={600} size="md" mb="xs">Requestor Configuration</Text>
                        <Text size="sm" c="dimmed" mb="md">
                            Select a Requestor Set to define who can perform the action. Cannot be used together with Target Relative Attribute.
                        </Text>
                        {hasFormValueForEntityRef(formData, RELATIVE_TARGET_ATTR) && hasFormValueForEntityRef(formData, REQUESTOR_SET_ATTR) && (
                            <Alert color="red" mb="md">
                                Cannot have both Requestor Set and Target Relative Attribute. Please clear one of them before saving.
                            </Alert>
                        )}
                        <Grid gutter="md">
                            {requestorFields.map((attr, index) => (
                                <Grid.Col span={{ base: 12, sm: 6 }} key={index}>
                                    {renderField(attr)}
                                </Grid.Col>
                            ))}
                        </Grid>
                    </Box>
                    <Divider my="lg" />
                </>
            )}

            {/* Target Configuration */}
            {targetFields.length > 0 && (
                <>
                    <Box mb="lg">
                        <Text fw={600} size="md" mb="xs">Target Configuration</Text>
                        <Text size="sm" c="dimmed" mb="md">
                            Configure what entities are affected and which attributes this policy rule applies to. Options are determined by the Target Set's FilterScope.
                        </Text>
                        {hasFormValueForEntityRef(formData, RELATIVE_TARGET_ATTR) && hasFormValueForEntityRef(formData, REQUESTOR_SET_ATTR) && (
                            <Alert color="red" mb="md">
                                Cannot have both Target Relative Attribute and Requestor Set. Please clear one of them before saving.
                            </Alert>
                        )}
                        {relativeTargetAttributeError && (
                            <Alert color="red" mb="md">
                                {relativeTargetAttributeError}
                            </Alert>
                        )}
                        <Grid gutter="md">
                            {targetFields.map((attr, index) => (
                                <Grid.Col span={{ base: 12, sm: 6 }} key={index}>
                                    {renderField(attr)}
                                </Grid.Col>
                            ))}
                        </Grid>
                    </Box>
                    <Divider my="lg" />
                </>
            )}

            {/* Workflow Configuration */}
            {workflowFields.length > 0 && (
                <>
                    <Box mb="lg">
                        <Text fw={600} size="md" mb="xs">Workflow Configuration</Text>
                        <Text size="sm" c="dimmed" mb="md">
                            Optional workflows to execute for request and authorization operations.
                        </Text>
                        <Grid gutter="md">
                            {workflowFields.map((attr, index) => (
                                <Grid.Col span={{ base: 12, sm: 6 }} key={index}>
                                    {renderField(attr)}
                                </Grid.Col>
                            ))}
                        </Grid>
                    </Box>
                    <Divider my="lg" />
                </>
            )}

            {/* Attribute Selection */}
            {attributeFields.length > 0 && (
                <>
                    <Box mb="lg">
                        <Text fw={600} size="md" mb="xs">Attribute Selection</Text>
                        <Text size="sm" c="dimmed" mb="md">
                            Choose which attributes this policy rule applies to. You can select specific attributes or apply to all attributes.
                        </Text>
                        {attributeFields.map((attr, index) => (
                            <React.Fragment key={index}>{renderField(attr)}</React.Fragment>
                        ))}
                    </Box>
                    <Divider my="lg" />
                </>
            )}

            {/* Action Permissions — 3-column compact grid */}
            {permissionFields.length > 0 && (
                <>
                    <Box mb="lg">
                        <Text fw={600} size="md" mb="xs">Action Permissions</Text>
                        <Grid gutter="md">
                            {permissionFields.map((attr, index) => (
                                <Grid.Col span={{ base: 12, sm: 4 }} key={index}>
                                    {renderField(attr)}
                                </Grid.Col>
                            ))}
                        </Grid>
                    </Box>
                    <Divider my="lg" />
                </>
            )}

            {/* Status */}
            {statusFields.length > 0 && (
                <Box mb="lg">
                    <Text fw={600} size="md" mb="xs">Status</Text>
                    <Grid gutter="md">
                        {statusFields.map((attr, index) => (
                            <Grid.Col span={{ base: 12, sm: 6 }} key={index}>
                                {renderField(attr)}
                            </Grid.Col>
                        ))}
                    </Grid>
                </Box>
            )}
        </>
    );
}

export const PolicyRuleFormLayout: React.FC<PolicyRuleFormLayoutProps> = ({
    schemaAttributes,
    formData,
    relativeTargetAttributeError,
    renderField,
    isStandard = false
}) => {
    const {
        idFields,
        basicFields,
        requestorFields,
        targetFields,
        workflowFields,
        attributeFields,
        permissionFields,
        statusFields
    } = categorizeFields(schemaAttributes, isStandard);

    if (isStandard) {
        return (
            <StandardLayout
                idFields={idFields}
                basicFields={basicFields}
                requestorFields={requestorFields}
                targetFields={targetFields}
                attributeFields={attributeFields}
                permissionFields={permissionFields}
                statusFields={statusFields}
                formData={formData}
                renderField={renderField}
            />
        );
    }

    return (
        <AdminLayout
            idFields={idFields}
            basicFields={basicFields}
            requestorFields={requestorFields}
            targetFields={targetFields}
            workflowFields={workflowFields}
            attributeFields={attributeFields}
            permissionFields={permissionFields}
            statusFields={statusFields}
            formData={formData}
            relativeTargetAttributeError={relativeTargetAttributeError}
            renderField={renderField}
        />
    );
};
